import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faDownload, faFileCode, faCompress } from '@fortawesome/free-solid-svg-icons';
import { resolveMediaUrl } from '../utils/taskHelpers';

// Swipe handling (touch screens only).
const AXIS_LOCK_PX = 8;         // movement before a touch counts as a swipe
const CLOSE_DISTANCE_PX = 110;  // drag down this far and let go to close
const FLICK_DISTANCE_PX = 40;   // ...or flick down at least this far
const FLICK_SPEED = 0.4;        // px per ms
const TURN_DISTANCE_PX = 50;    // sideways swipe to the next/previous item
const LIFT_DISTANCE_PX = 70;    // swipe a video up this far for full screen
const VIDEO_CONTROLS_PX = 64;   // bottom strip of a video left to its own controls
const SETTLE_MS = 180;

/*
 * Full screen for a video, turned sideways when it is wider than it is tall.
 *
 * Android's browsers can do both for real: full screen, then lock the screen to
 * landscape. iPhone Safari can do neither from a page — it has no full screen
 * for an element and no way to turn the screen — so there, and anywhere the
 * real thing is refused, `fallback` is called and the viewer turns the video
 * itself (see .lb-landscape in tasks.css).
 */
const enterVideoFullscreen = (video, fallback) => {
    const wide = video.videoWidth >= video.videoHeight;
    const turn = () => {
        try {
            const locking = wide && window.screen.orientation?.lock?.('landscape');
            if (locking?.catch) locking.catch(() => {});
        } catch (err) { /* not supported here: stays as the phone is held */ }
    };
    if (!video.requestFullscreen) {
        fallback();
        return;
    }
    try {
        const entering = video.requestFullscreen();
        if (entering?.then) entering.then(turn).catch(fallback);
    } catch (err) {
        fallback();
    }
};

// The page is pinch-zoomed in: one finger pans the page then, so leave it be.
const isPageZoomed = () => (window.visualViewport?.scale || 1) > 1.02;

/**
 * In-page viewer for task media. Opening attachments in a new tab loses the
 * page you were on and drops you at a bare S3 url, so images and videos are
 * shown here instead.
 *
 * On a phone the controls sit in a bar at the top (clear of the notch) and the
 * media can be swiped down to close, or sideways to the next one. A video can
 * also be swiped up to go full screen.
 *
 * items: [{ _id, url, fileName, type }]
 */
const MediaLightbox = ({ items, index, onClose, onIndexChange }) => {
    const total = items?.length || 0;
    const item = items?.[index];

    const overlayRef = useRef(null);
    const stageRef = useRef(null);
    const backdropRef = useRef(null);

    // The viewer's own full screen for a video, used where the browser has
    // none to offer (iPhone). Belongs to one video: turning to another item
    // leaves it.
    const [landscape, setLandscape] = useState(false);
    const landscapeRef = useRef(false);
    landscapeRef.current = landscape;
    useEffect(() => { setLandscape(false); }, [index]);

    const next = useCallback(() => {
        if (total > 1) onIndexChange((index + 1) % total);
    }, [index, total, onIndexChange]);

    const prev = useCallback(() => {
        if (total > 1) onIndexChange((index - 1 + total) % total);
    }, [index, total, onIndexChange]);

    // Keyboard control, and don't let the page scroll behind the overlay.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (landscapeRef.current) setLandscape(false);
                else onClose();
            }
            else if (e.key === 'ArrowRight') next();
            else if (e.key === 'ArrowLeft') prev();
        };
        document.addEventListener('keydown', onKey);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [onClose, next, prev]);

    // The latest handlers, so the swipe listeners below aren't re-attached
    // (dropping a drag in progress) every time the page behind re-renders.
    const latest = useRef({ onClose, next, prev });
    latest.current = { onClose, next, prev };

    // Swipe down to close, sideways to turn. Native listeners rather than
    // React's: touchmove has to be non-passive so the browser's own
    // pull-to-refresh doesn't fire while the media is being dragged.
    const hasItem = Boolean(item);
    useEffect(() => {
        const overlay = overlayRef.current;
        const stage = stageRef.current;
        const backdrop = backdropRef.current;
        if (!hasItem || !overlay || !stage || !backdrop) return undefined;

        let touch = null;
        let closeTimer = null;

        const place = (dy, animate) => {
            const transition = animate ? `transform ${SETTLE_MS}ms ease-out` : 'none';
            stage.style.transition = transition;
            backdrop.style.transition = animate ? `opacity ${SETTLE_MS}ms ease-out` : 'none';
            if (dy <= 0) {
                // Only a video lifts (towards full screen), and only a little.
                const lift = dy < 0 && stage.querySelector('video') ? Math.max(dy * 0.35, -60) : 0;
                stage.style.transform = lift ? `translate3d(0, ${lift}px, 0)` : '';
                backdrop.style.opacity = '';
                return;
            }
            const progress = Math.min(1, dy / (window.innerHeight * 0.6));
            stage.style.transform = `translate3d(0, ${dy}px, 0) scale(${1 - progress * 0.15})`;
            backdrop.style.opacity = String(1 - progress * 0.85);
        };

        const onStart = (e) => {
            touch = null;
            // Turned sideways, every swipe belongs to the video's own controls.
            if (landscapeRef.current) return;
            if (closeTimer || e.touches.length !== 1 || isPageZoomed()) return;
            const t = e.touches[0];
            const target = e.target;
            if (target.closest?.('.lb-top, .lb-nav, .lb-close')) return;
            if (target.tagName === 'VIDEO' && t.clientY > target.getBoundingClientRect().bottom - VIDEO_CONTROLS_PX) return;
            touch = { x: t.clientX, y: t.clientY, at: Date.now(), axis: null, dx: 0, dy: 0 };
        };

        const onMove = (e) => {
            if (!touch) return;
            if (e.touches.length !== 1) { // a second finger: that's a pinch
                touch = null;
                overlay.classList.remove('lb-dragging');
                place(0, true);
                return;
            }
            const t = e.touches[0];
            touch.dx = t.clientX - touch.x;
            touch.dy = t.clientY - touch.y;
            if (e.cancelable) e.preventDefault();
            if (!touch.axis) {
                if (Math.max(Math.abs(touch.dx), Math.abs(touch.dy)) < AXIS_LOCK_PX) return;
                touch.axis = Math.abs(touch.dy) > Math.abs(touch.dx) ? 'y' : 'x';
                if (touch.axis === 'y') overlay.classList.add('lb-dragging');
            }
            if (touch.axis === 'y') place(touch.dy, false);
        };

        const onEnd = () => {
            const done = touch;
            touch = null;
            if (!done || !done.axis) return;
            overlay.classList.remove('lb-dragging');

            if (done.axis === 'x') {
                if (done.dx <= -TURN_DISTANCE_PX) latest.current.next();
                else if (done.dx >= TURN_DISTANCE_PX) latest.current.prev();
                return;
            }

            const video = stage.querySelector('video');
            if (video && done.dy <= -LIFT_DISTANCE_PX) {
                place(0, true);
                enterVideoFullscreen(video, () => setLandscape(true));
                return;
            }

            const speed = done.dy / Math.max(1, Date.now() - done.at);
            const shouldClose = done.dy >= CLOSE_DISTANCE_PX || (done.dy >= FLICK_DISTANCE_PX && speed >= FLICK_SPEED);
            if (!shouldClose) {
                place(0, true);
                return;
            }
            overlay.classList.add('lb-leaving');
            place(window.innerHeight, true);
            closeTimer = setTimeout(() => latest.current.onClose(), SETTLE_MS);
        };

        const onCancel = () => {
            if (!touch) return;
            touch = null;
            overlay.classList.remove('lb-dragging');
            place(0, true);
        };

        overlay.addEventListener('touchstart', onStart, { passive: true });
        overlay.addEventListener('touchmove', onMove, { passive: false });
        overlay.addEventListener('touchend', onEnd);
        overlay.addEventListener('touchcancel', onCancel);

        return () => {
            clearTimeout(closeTimer);
            overlay.removeEventListener('touchstart', onStart);
            overlay.removeEventListener('touchmove', onMove);
            overlay.removeEventListener('touchend', onEnd);
            overlay.removeEventListener('touchcancel', onCancel);
            overlay.classList.remove('lb-dragging');
            place(0, false);
        };
    }, [hasItem]);

    if (!item) return null;

    const src = resolveMediaUrl(item.url);
    const name = item.fileName || 'Attachment';

    // In a portal: a `position: fixed` overlay rendered inside a message bubble
    // is at the mercy of every ancestor's transform and stacking order.
    return createPortal(
        <div
            className={`lb-overlay${landscape ? ' lb-landscape' : ''}`}
            ref={overlayRef}
            onClick={landscape ? undefined : onClose}
            role="dialog" aria-modal="true" aria-label={name}
        >
            <div className="lb-backdrop" ref={backdropRef} />

            {/* Phones only (see tasks.css): the controls in one bar at the top. */}
            <div className="lb-top" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="lb-top-btn" onClick={onClose} aria-label="Close">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <div className="lb-top-title">
                    <span className="lb-top-name">{name}</span>
                    {total > 1 && <span className="lb-top-count">{index + 1} of {total}</span>}
                </div>
                <a className="lb-top-btn" href={src} download={item.fileName || true} aria-label="Download">
                    <FontAwesomeIcon icon={faDownload} />
                </a>
            </div>

            <button className="lb-close" onClick={onClose} title="Close (Esc)" aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
            </button>

            {total > 1 && (
                <button
                    className="lb-nav prev"
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    title="Previous (←)" aria-label="Previous"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            )}

            {/* Stop propagation so clicking the media itself doesn't close it. */}
            <figure className="lb-stage" ref={stageRef} onClick={(e) => e.stopPropagation()}>
                {landscape && (
                    <button type="button" className="lb-landscape-exit" onClick={() => setLandscape(false)} aria-label="Exit full screen">
                        <FontAwesomeIcon icon={faCompress} />
                    </button>
                )}
                {item.type === 'video' ? (
                    <video src={src} controls autoPlay playsInline className="lb-media" />
                ) : item.type === 'document' ? (
                    // Not embedded — see the note in TaskMediaGrid on why an
                    // uploaded HTML document opens externally rather than in
                    // an iframe on our own page.
                    <a href={src} target="_blank" rel="noopener noreferrer" className="lb-doc">
                        <FontAwesomeIcon icon={faFileCode} />
                        <span>Open {item.fileName || 'document'}</span>
                    </a>
                ) : (
                    <img src={src} alt={item.fileName || 'attachment'} className="lb-media" draggable={false} />
                )}

                <figcaption className="lb-caption">
                    <span className="lb-name">{name}</span>
                    <span className="lb-actions">
                        {total > 1 && <span className="lb-counter">{index + 1} / {total}</span>}
                        <a href={src} download={item.fileName || true} onClick={(e) => e.stopPropagation()} title="Download">
                            <FontAwesomeIcon icon={faDownload} />
                        </a>
                    </span>
                </figcaption>
            </figure>

            {total > 1 && (
                <button
                    className="lb-nav next"
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    title="Next (→)" aria-label="Next"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            )}
        </div>,
        document.body
    );
};

export default MediaLightbox;
